// @ts-nocheck
import { Card, Code, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { InvoiceCard, TransactionList, BudgetTracker } from '@/components/advanced/financial';

export default function FinancialPage() {
  // Invoice mock data
  const invoice = {
    id: 'inv-1024',
    number: 'INV-2024-1024',
    status: 'sent' as const,
    date: new Date('2024-01-05'),
    dueDate: new Date('2024-02-05'),
    customer: {
      id: 'cust-1',
      name: 'Acme Corporation',
      email: 'billing@acme.example',
      address: '500 Market St, Springfield',
    },
    items: [
      { id: '1', description: 'Consulting services', quantity: 20, unitPrice: 150, total: 3000 },
      { id: '2', description: 'Software license', quantity: 1, unitPrice: 1200, total: 1200 },
      { id: '3', description: 'Support plan (annual)', quantity: 1, unitPrice: 800, total: 800 },
    ],
    subtotal: 5000,
    tax: 500,
    discount: 250,
    total: 5250,
    currency: 'USD',
    notes: 'Payment due within 30 days. Thank you for your business.',
  };

  // Transaction mock data
  const transactions = [
    {
      id: 't1',
      type: 'income' as const,
      category: 'Salary',
      description: 'Monthly payroll deposit',
      amount: 4500,
      date: new Date('2024-01-31'),
      status: 'completed' as const,
      paymentMethod: 'bank' as const,
      counterparty: 'Acme Corp',
    },
    {
      id: 't2',
      type: 'expense' as const,
      category: 'Office Supplies',
      description: 'Printer toner and paper',
      amount: 132.5,
      date: new Date('2024-01-31'),
      status: 'completed' as const,
      paymentMethod: 'card' as const,
      counterparty: 'OfficeMart',
    },
    {
      id: 't3',
      type: 'expense' as const,
      category: 'Software',
      description: 'SaaS subscription',
      amount: 49,
      date: new Date('2024-01-30'),
      status: 'pending' as const,
      paymentMethod: 'card' as const,
    },
    {
      id: 't4',
      type: 'transfer' as const,
      category: 'Savings',
      description: 'Transfer to savings account',
      amount: 1000,
      date: new Date('2024-01-30'),
      status: 'completed' as const,
      paymentMethod: 'transfer' as const,
    },
  ];

  // Budget mock data
  const budgetCategories = [
    { id: 'b1', name: 'Marketing', color: 'blue', budgeted: 5000, spent: 3200 },
    { id: 'b2', name: 'Operations', color: 'teal', budgeted: 8000, spent: 6900 },
    { id: 'b3', name: 'Travel', color: 'grape', budgeted: 2000, spent: 2100 },
    { id: 'b4', name: 'Equipment', color: 'orange', budgeted: 4000, spent: 1500 },
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Financial Components</Title>
        <Text c="dimmed" size="sm">
          Components for invoices, transactions, and budgets — formatting currency, statuses, and
          spending progress out of the box
        </Text>
      </Stack>

      {/* Invoice Card */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>InvoiceCard</Title>
            <Text size="sm" c="dimmed">
              Display an invoice with status, customer, line items, and totals. Supports summary,
              detailed, and compact variants.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (detailed):</Text>
            <InvoiceCard
              invoice={invoice}
              variant="detailed"
              onDownload={() => {}}
              onPrint={() => {}}
              onMarkPaid={() => {}}
            />
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (compact):</Text>
            <InvoiceCard
              invoice={{ ...invoice, id: 'inv-1025', number: 'INV-2024-1025', status: 'overdue' }}
              variant="compact"
              onView={() => {}}
              onMarkPaid={() => {}}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const invoice = {
  id: 'inv-1024',
  number: 'INV-2024-1024',
  status: 'sent',
  date: new Date('2024-01-05'),
  dueDate: new Date('2024-02-05'),
  customer: {
    id: 'cust-1',
    name: 'Acme Corporation',
    email: 'billing@acme.example',
    address: '500 Market St, Springfield',
  },
  items: [
    { id: '1', description: 'Consulting services', quantity: 20, unitPrice: 150, total: 3000 },
    { id: '2', description: 'Software license', quantity: 1, unitPrice: 1200, total: 1200 },
  ],
  subtotal: 5000,
  tax: 500,
  discount: 250,
  total: 5250,
  currency: 'USD',
  notes: 'Payment due within 30 days.',
};

<InvoiceCard
  invoice={invoice}
  variant="detailed"
  onDownload={() => {}}
  onMarkPaid={() => {}}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • invoice - InvoiceData object (required)
              <br />
              • variant - 'summary' | 'detailed' | 'compact'
              <br />
              • onView / onEdit / onDelete - action handlers
              <br />
              • onDownload / onPrint / onSend - action handlers
              <br />
              • onMarkPaid / onDuplicate - action handlers
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Transaction List */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>TransactionList</Title>
            <Text size="sm" c="dimmed">
              List of income, expense, and transfer transactions with optional date grouping and a
              running balance summary.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <TransactionList
              transactions={transactions}
              showBalance
              balance={3318.5}
              groupByDate
              onTransactionClick={() => {}}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const transactions = [
  {
    id: 't1',
    type: 'income',
    category: 'Salary',
    description: 'Monthly payroll deposit',
    amount: 4500,
    date: new Date('2024-01-31'),
    status: 'completed',
    paymentMethod: 'bank',
    counterparty: 'Acme Corp',
  },
  {
    id: 't2',
    type: 'expense',
    category: 'Office Supplies',
    description: 'Printer toner and paper',
    amount: 132.5,
    date: new Date('2024-01-31'),
    status: 'completed',
    paymentMethod: 'card',
  },
];

<TransactionList
  transactions={transactions}
  showBalance
  balance={3318.5}
  groupByDate
  onTransactionClick={(t) => console.log(t)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • transactions - Transaction[] (required)
              <br />
              • groupByDate - Group rows under date headers
              <br />
              • showBalance - Show balance/income/expense summary
              <br />
              • balance - Current balance value
              <br />
              • variant - 'default' | 'compact'
              <br />
              • onTransactionClick - Row click handler
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Budget Tracker */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>BudgetTracker</Title>
            <Text size="sm" c="dimmed">
              Track spending against budget per category with an overview ring and color-coded
              progress. Supports cards, list, and compact variants.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (cards):</Text>
            <BudgetTracker
              title="Q1 Budget"
              period="January 2024"
              categories={budgetCategories}
              totalBudget={19000}
              totalSpent={13700}
              variant="cards"
              onCategoryClick={() => {}}
            />
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (compact):</Text>
            <BudgetTracker
              title="Department Spend"
              categories={budgetCategories}
              totalBudget={19000}
              totalSpent={13700}
              variant="compact"
              showOverview={false}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const categories = [
  { id: 'b1', name: 'Marketing', color: 'blue', budgeted: 5000, spent: 3200 },
  { id: 'b2', name: 'Operations', color: 'teal', budgeted: 8000, spent: 6900 },
  { id: 'b3', name: 'Travel', color: 'grape', budgeted: 2000, spent: 2100 },
  { id: 'b4', name: 'Equipment', color: 'orange', budgeted: 4000, spent: 1500 },
];

<BudgetTracker
  title="Q1 Budget"
  period="January 2024"
  categories={categories}
  totalBudget={19000}
  totalSpent={13700}
  variant="cards"
  onCategoryClick={(c) => console.log(c)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • categories - BudgetCategory[] (required)
              <br />
              • totalBudget - Total budget amount (required)
              <br />
              • totalSpent - Total spent amount (required)
              <br />
              • title / period - Header labels
              <br />
              • variant - 'cards' | 'list' | 'compact'
              <br />
              • showOverview - Show the summary ring
              <br />
              • onCategoryClick - Category click handler
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Billing & Invoicing</Text>
              <Text size="xs" c="dimmed">
                Use InvoiceCard on customer billing pages to show invoice status, due dates, and
                line-item breakdowns with download and payment actions.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Account Activity</Text>
              <Text size="xs" c="dimmed">
                Use TransactionList for account ledgers and statements, grouping income and expenses
                by date with a running balance.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Budget Monitoring</Text>
              <Text size="xs" c="dimmed">
                Use BudgetTracker on finance dashboards to monitor departmental spend against budget
                and flag categories that are over.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
